import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import Icon from '../../components/AppIcon';
import ScheduleStep from '../service-booking-wizard/components/ScheduleStep';
import PaymentStep from '../service-booking-wizard/components/PaymentStep';
import { useAuth } from '../../contexts/AuthContext';

const STORAGE_KEY = 'plumberpro_booking_draft';

const QuoteAcceptBookingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { profile } = useAuth();
  const [step, setStep] = useState(() =>
    searchParams.get('step') === 'payment' ? 'payment' : 'schedule'
  );
  const [bookingData, setBookingData] = useState(null);

  useEffect(() => {
    const draftFromState = location.state?.bookingData;
    if (draftFromState?.fromAcceptedQuote && draftFromState?.sourceAppointmentId) {
      setBookingData(draftFromState);
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          currentStep: 1,
          bookingData: draftFromState,
          timestamp: Date.now(),
        })
      );
      return;
    }

    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      const savedDraft = saved?.bookingData;
      if (savedDraft?.fromAcceptedQuote && savedDraft?.sourceAppointmentId) {
        setBookingData(savedDraft);
        if (searchParams.get('step') === 'payment' && savedDraft?.timeSlot) {
          setStep('payment');
        }
        return;
      }
    } catch {
      // ignore
    }

    navigate('/', { replace: true });
  }, [location.state, navigate, searchParams]);

  useEffect(() => {
    if (!bookingData) return;
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        currentStep: step === 'payment' ? 2 : 1,
        bookingData,
        timestamp: Date.now(),
      })
    );
  }, [bookingData, step]);

  if (!bookingData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const handleUpdate = (patch) => {
    setBookingData((prev) => ({ ...prev, ...patch }));
  };

  const handleScheduleNext = () => {
    if (!bookingData?.timeSlot) return;
    setStep('payment');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToSchedule = () => {
    setStep('schedule');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader
        userRole={profile?.role || 'client'}
        userName={bookingData.fullName || profile?.full_name || 'Client'}
      />

      <main className="main-content max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="mb-6 flex items-center gap-3">
          <button
            type="button"
            onClick={() => {
              if (step === 'payment') handleBackToSchedule();
              else navigate(-1);
            }}
            className="p-2 rounded-lg hover:bg-muted text-foreground"
            aria-label="Retour"
          >
            <Icon name="ArrowLeft" size={20} />
          </button>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">
              Acceptation du devis {bookingData.quoteReference || ''}
            </p>
            <h1 className="text-xl sm:text-2xl font-semibold text-foreground">
              {step === 'schedule' ? 'Choisissez votre créneau' : 'Paiement'}
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-6 text-sm">
          <span
            className={`px-3 py-1 rounded-full ${
              step === 'schedule'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground'
            }`}
          >
            1. Créneau
          </span>
          <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
          <span
            className={`px-3 py-1 rounded-full ${
              step === 'payment'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground'
            }`}
          >
            2. Paiement
          </span>
        </div>

        {step === 'schedule' ? (
          <ScheduleStep
            data={bookingData}
            onUpdate={handleUpdate}
            onNext={handleScheduleNext}
            onBack={() => navigate(-1)}
            isActive
            showBack
            heading="Choisissez votre créneau"
            subheading="Sélectionnez le jour et l’horaire de votre intervention."
          />
        ) : (
          <PaymentStep
            data={bookingData}
            onUpdate={handleUpdate}
            onBack={handleBackToSchedule}
            onBookingComplete={() => {}}
            currentStep={2}
            isActive
          />
        )}
      </main>
    </div>
  );
};

export default QuoteAcceptBookingPage;
